import { useCallback, useEffect, useState } from 'react';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { pressReleasesApi } from '../pressReleaseApi';
import type { PressReleasePostingItem } from '../pressReleaseTypes';
import { PostingsModal } from './PostingsModal';

/**
 * Where the release went out — one row per channel, posted or not, and when and by whom.
 *
 * **Edit is the checklist, on its own key** (`record-postings`), and is DISABLED with the server's
 * reason until the release is published — a draft has not gone out. A release with no channel
 * at all says so, and points at Settings rather than offering an Edit with nothing to tick.
 */
export function PostingsCard({
  releaseId,
  releaseTitle,
  lockReason,
}: {
  releaseId: string;
  releaseTitle: string;
  /** Why the checklist cannot change right now (a draft, or deleted), or null. */
  lockReason: string | null;
}) {
  const [lines, setLines] = useState<PressReleasePostingItem[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      pressReleasesApi
        .postings(releaseId, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setLines(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load the channels.', 0));
        });
    },
    [releaseId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const posted = lines?.filter((l) => l.isPosted).length;
  const hasChannels = lines !== null && lines.length > 0;

  return (
    <>
      <ProfileCard
        id="press-release-profile-channels"
        title="Channels"
        count={posted}
        onEdit={hasChannels ? () => setEditing(true) : undefined}
        editLabel="Record postings"
        editPermission="press-releases.record-postings"
        editDisabledReason={lockReason}
        empty={
          lines !== null && lines.length === 0
            ? {
                title: 'No channels yet.',
                description: 'Add where press releases are posted — Facebook, Website, Instagram — under Public Relations › Settings › Channels.',
              }
            : undefined
        }
      >
        {error && lines === null ? (
          <div className="p-4">
            <ErrorState message={error.message} onRetry={() => load()} />
          </div>
        ) : lines === null ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true" aria-label="Loading channels">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : (
          <PreviewTable
            variant="table-display"
            rows={lines.map((line) => ({
              label: line.channelIsActive ? line.channelName : `${line.channelName} (deleted channel)`,
              value: line.isPosted ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <StatusText tone="active">Posted</StatusText>
                  {line.postedAt != null ? (
                    <time dateTime={toIso(line.postedAt)} className="font-mono tabular-nums">
                      {formatDateTime(line.postedAt)}
                    </time>
                  ) : null}
                  {line.postedByName ? <span>by {line.postedByName}</span> : null}
                </span>
              ) : (
                <span className="text-slate-400">Not posted</span>
              ),
            }))}
          />
        )}
      </ProfileCard>

      {editing && lines !== null ? (
        <PostingsModal
          releaseId={releaseId}
          releaseTitle={releaseTitle}
          lines={lines}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            setLines(next);
          }}
        />
      ) : null}
    </>
  );
}
